import React, { FC, useMemo, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import MainLayout from '../pages/layouts/MainLayout';
import { QueryParamProvider } from 'use-query-params';
import { parse, stringify } from 'query-string';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { AppLoader, ProtectedRoutesMiddleware, isAdminForActiveCompany } from '@jaam/web-core';
import Cookies from 'universal-cookie';
import { useAuth } from '../hooks/useAuth';
import { PAYROLL_ROUTES } from '../constants/payrollAccessRoutes.constants';
import { NavigationProvider } from '../context/NavigationProvider';

const cookies = new Cookies();

/** Redirige vers company/plan si la compagnie n'a pas d'abonnement actif (activeSub null). */
function companyHasPlan(
  activeCompany: { planId?: number | null; activeSub?: unknown } | null | undefined,
): boolean {
  if (!activeCompany) return false;
  const withSub = activeCompany as { activeSub?: unknown; planId?: number | null };
  if (withSub.activeSub != null) return true;
  const planId = withSub.planId;
  return planId != null && planId > 0;
}

const ProtectedRoutes: FC = () => {
  const { activeCompany, isPending, isAuthenticated, isError } = useAuth();

  const authState = useMemo(
    () => ({
      isAuthenticated: !!isAuthenticated,
      activeCompany: activeCompany ?? null,
      isLoading: isPending,
      isError: !!isError,
    }),
    [isAuthenticated, activeCompany, isPending, isError],
  );

  const getCustomRedirect = useCallback(
    ({
      pathname,
      authState: state,
    }: {
      pathname: string;
      authState: {
        activeCompany?: {
          id?: number;
          planId?: number | null;
          activeSub?: unknown;
          roles?: { name?: string }[];
        } | null;
      };
    }) => {
      const company = state.activeCompany;
      if (!company?.id) return null;
      if (companyHasPlan(company)) return null;
      if (pathname === '/company/plan') return null;
      if (pathname === '/company/contact-admin') return null;
      if (isAdminForActiveCompany(company)) {
        return {
          type: 'internal' as const,
          to: '/company/plan',
          replace: true,
          state: { companyId: company.id },
        };
      }
      return {
        type: 'internal' as const,
        to: '/company/contact-admin',
        replace: true,
      };
    },
    [],
  );

  const config = useMemo(
    () => ({
      authMode: 'client-app' as const,
      authAppBaseUrl: process.env.NX_AUTH_PUBLIC_URL,
      appReferer: 'payrollr',
      onClearAuth: () => {
        cookies.remove('ACCESS_TOKEN');
        cookies.remove('REFRESH_TOKEN');
      },
      LoaderComponent: AppLoader,
      getCustomRedirect,
      routeAccess: {
        routes: PAYROLL_ROUTES,
        defaultRedirect: '/home',
        noAccessBehavior: 'redirect' as const,
      },
    }),
    [getCustomRedirect],
  );

  return (
    <ProtectedRoutesMiddleware authState={authState} config={config}>
      <NavigationProvider>
        <MainLayout>
          <QueryParamProvider
            options={{
              searchStringToObject: parse,
              objectToSearchString: stringify,
            }}
            adapter={ReactRouter6Adapter}
          >
            <Outlet />
          </QueryParamProvider>
        </MainLayout>
      </NavigationProvider>
    </ProtectedRoutesMiddleware>
  );
};

export default ProtectedRoutes;
